import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { MpsNotificationService } from '../../../../mps/services/mps-notification.service';
import { MpsUserService } from '../../../../mps/services/mps-user.service';
import { User } from '../../../administration/users/user.model';
import { UsersService } from '../../../administration/users/users.service';
import { MpsRoleHelper } from '../../../helpers/mps-role.helper';
import { MpsUser } from '../../../models/mps-user.model';
import { ViewAsService } from '../../../services/view-as.service';

@Component({
  selector: 'app-admin-view-as-banner',
  templateUrl: './admin-view-as-banner.component.html',
  styleUrls: ['./admin-view-as-banner.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
  ],
})
export class AdminViewAsBannerComponent implements OnInit {
  private _usersService = inject(UsersService);
  private _viewAsService = inject(ViewAsService);
  private _mpsUserService = inject(MpsUserService);
  private _notificationService = inject(MpsNotificationService);
  private _translateService = inject(TranslateService);

  search = new FormControl('');
  usersToSelect = signal<User[]>([]);
  viewedUser = this._viewAsService.viewedUser;
  isViewingAsOther = this._viewAsService.isViewingAsOther;
  readonly canViewAs = environment.allowViewAs;

  get isAdmin(): boolean {
    const userData = this._mpsUserService.getUser();
    if (!userData) return false;
    const user = new MpsUser(userData);
    return user.isAdmin();
  }

  ngOnInit(): void {
    this.search.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((searchInput): searchInput is string => typeof searchInput === 'string' && searchInput.length >= 3)
      )
      .subscribe((searchInput) => {
        const userRoleId = MpsRoleHelper.getRoleIdFromCode(MpsRoleHelper.USER);
        this._usersService.all(searchInput, userRoleId).subscribe({
          next: (users) => {
            this.usersToSelect.set(users);
          },
          error: () => {
            this.usersToSelect.set([]);
            this._notificationService.error(this._translateService.instant('messages.api_response.generic_error'));
          },
        });
      });
  }

  displayUser(user?: User | null): string {
    return user ? `${user.igg} - ${user.firstname} ${user.lastname}` : '';
  }

  selectUser(user: User): void {
    this._viewAsService.setViewedUser(user);
    this.search.setValue('');
    this.usersToSelect.set([]);
  }

  clearViewedUser(): void {
    this._viewAsService.clearViewedUser();
  }

  getViewedUserDisplayName(): string {
    const user = this.viewedUser();
    if (!user) return '';
    return `${user.firstname} ${user.lastname} (${user.igg})`;
  }
}
